import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Save, Eye, EyeOff, ChevronUp, ChevronDown, Menu as MenuIcon, Trash2 } from "lucide-react";

/**
 * Menu editor.
 * -----------------------------------------------------------------------
 * The CECIT nav is defined statically in Header.jsx / Footer.jsx (so labels
 * remain fully bilingual via the i18n context), but every top-level group
 * and sub-item is tagged with a stable `key`. This admin surfaces those
 * keys as toggle rows and stores two arrays in site_settings.menu_config:
 *
 *   { hidden: [key,…], order: [flat list of all keys] }
 *
 * for both `header` and `footer` sections.
 *
 * From the editor's point of view, "Eliminar" is functionally the same as
 * "Ocultar" (the underlying tree is defined in code) but expresses stronger
 * intent — it prompts for confirmation and the item is removed from the
 * visible list immediately.
 */

const HEADER_GROUPS = [
  { key: "camara", label: "La Cámara", items: [
    { key: "camara.overview", label: "La Cámara — Presentación" },
    { key: "camara.mision", label: "Misión y objetivos" },
    { key: "camara.contacto", label: "Contacto" },
  ]},
  { key: "servicios", label: "Servicios", items: [
    { key: "servicios.info", label: "Información empresarial" },
    { key: "servicios.training", label: "Plataforma de formación" },
    { key: "servicios.visa", label: "Gestión de visados" },
    { key: "servicios.jobs", label: "Bolsa de empleo" },
    { key: "servicios.opportunities", label: "Oportunidades de negocio" },
  ]},
  { key: "informacion", label: "Información", items: [
    { key: "informacion.anuario", label: "Anuario" },
    { key: "informacion.newsletters", label: "Newsletters" },
    { key: "informacion.noticias", label: "Noticias de socios" },
    { key: "informacion.invertir", label: "Invertir en Marruecos" },
  ]},
  { key: "actividades", label: "Actividades", items: [
    { key: "actividades.all", label: "Todas" },
    { key: "actividades.encuentros", label: "Encuentros empresariales" },
    { key: "actividades.networking", label: "Networking" },
    { key: "actividades.seminarios", label: "Seminarios" },
    { key: "actividades.salones_ferias", label: "Salones, ferias y foros" },
    { key: "actividades.visitas_reuniones", label: "Visitas y reuniones" },
    { key: "actividades.actos_institucionales", label: "Actos institucionales" },
    { key: "actividades.webinars", label: "Webinars" },
  ]},
  { key: "socios", label: "Socios", items: [
    { key: "socios.ventajas", label: "Ventajas de Socio" },
    { key: "socios.standard", label: "Socios Standard" },
    { key: "socios.premium", label: "Socios Premium" },
    { key: "socios.buscador", label: "Buscador de socios" },
  ]},
];

const FOOTER_GROUPS = [
  { key: "footer.camara", label: "La Cámara (columna)", items: [
    { key: "footer.camara.overview", label: "Presentación" },
    { key: "footer.camara.mision", label: "Misión y objetivos" },
    { key: "footer.camara.contacto", label: "Contacto" },
  ]},
  { key: "footer.servicios", label: "Servicios (columna)", items: [
    { key: "footer.servicios.info", label: "Información empresarial" },
    { key: "footer.servicios.training", label: "Plataforma de formación" },
    { key: "footer.servicios.visa", label: "Gestión de visados" },
    { key: "footer.servicios.jobs", label: "Bolsa de empleo" },
  ]},
  { key: "footer.socios", label: "Socios (columna)", items: [
    { key: "footer.socios.ventajas", label: "Ventajas de Socio" },
    { key: "footer.socios.standard", label: "Socios Standard" },
    { key: "footer.socios.premium", label: "Socios Premium" },
    { key: "footer.socios.buscador", label: "Buscador de socios" },
  ]},
  { key: "footer.informacion", label: "Información (columna)", items: [
    { key: "footer.informacion.anuario", label: "Anuario" },
    { key: "footer.informacion.newsletters", label: "Newsletters" },
    { key: "footer.informacion.noticias", label: "Noticias de socios" },
    { key: "footer.informacion.invertir", label: "Invertir en Marruecos" },
  ]},
  { key: "footer.contact", label: "Bloque Contacto (dirección/tel/email)", items: [] },
  { key: "footer.social", label: "Bloque Redes sociales", items: [] },
  { key: "footer.partners", label: "Bloque Logos de partners", items: [] },
  { key: "footer.legal", label: "Enlaces legales inferiores", items: [
    { key: "footer.legal.privacy", label: "Política de Privacidad" },
    { key: "footer.legal.cookies", label: "Política de Cookies" },
    { key: "footer.legal.notice", label: "Aviso Legal" },
  ]},
];

export default function MenusAdmin() {
  const [config, setConfig] = useState({ header: { hidden: [], order: [] }, footer: { hidden: [], order: [] } });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [ok, setOk] = useState(false);
  const [settingsFull, setSettingsFull] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettingsFull(data);
      const mc = data.menu_config || {};
      setConfig({
        header: { hidden: mc.header?.hidden || [], order: mc.header?.order || [] },
        footer: { hidden: mc.footer?.hidden || [], order: mc.footer?.order || [] },
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError(""); setOk(false);
    try {
      await api.put("/admin/settings", { ...settingsFull, menu_config: config });
      setOk(true);
      setTimeout(() => setOk(false), 2200);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const toggleHidden = (section, key) => {
    setConfig((prev) => {
      const hid = new Set(prev[section].hidden);
      hid.has(key) ? hid.delete(key) : hid.add(key);
      return { ...prev, [section]: { ...prev[section], hidden: [...hid] } };
    });
  };

  const removeEntry = (section, key, label) => {
    if (!window.confirm(`¿Eliminar "${label}" del menú? Podrá restaurarlo pulsando el ojo en la fila.`)) return;
    setConfig((prev) => {
      const hid = new Set(prev[section].hidden);
      hid.add(key);
      return { ...prev, [section]: { ...prev[section], hidden: [...hid] } };
    });
  };

  // The `order` array holds a flat list of keys (both top-level group keys
  // AND sub-item keys). We swap adjacent siblings within the same tree level.
  const move = (section, key, dir, siblings) => {
    setConfig((prev) => {
      const baseGroups = section === "header" ? HEADER_GROUPS : FOOTER_GROUPS;
      const defaultFlat = () => {
        const out = [];
        for (const g of baseGroups) { out.push(g.key); for (const it of g.items) out.push(it.key); }
        return out;
      };
      let baseOrder = prev[section].order.length ? [...prev[section].order] : defaultFlat();
      if (!baseOrder.includes(key)) baseOrder.push(key);
      const siblingKeys = siblings || baseGroups.map((g) => g.key);
      const idxInSiblings = siblingKeys.indexOf(key);
      const newIdxInSiblings = idxInSiblings + dir;
      if (newIdxInSiblings < 0 || newIdxInSiblings >= siblingKeys.length) return prev;
      const otherKey = siblingKeys[newIdxInSiblings];
      if (!baseOrder.includes(otherKey)) baseOrder.push(otherKey);
      const i = baseOrder.indexOf(key);
      const j = baseOrder.indexOf(otherKey);
      [baseOrder[i], baseOrder[j]] = [baseOrder[j], baseOrder[i]];
      return { ...prev, [section]: { ...prev[section], order: baseOrder } };
    });
  };

  if (loading) return <p className="text-gray-500">Cargando…</p>;

  return (
    <div data-testid="cms-menus">
      <div className="flex justify-between items-start mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-[#0F2040] flex items-center gap-2">
            <MenuIcon className="w-5 h-5" /> Menús
          </h1>
          <p className="text-sm text-gray-600">Mostrar/ocultar, reordenar y eliminar entradas del header y footer.</p>
        </div>
        <div className="flex gap-2 items-center">
          {ok && <span className="text-xs text-emerald-600" data-testid="menus-saved">Guardado ✓</span>}
          <button
            onClick={save}
            disabled={saving}
            data-testid="menus-save"
            className="bg-[#C8102E] text-white px-5 py-2.5 text-sm inline-flex items-center gap-2 hover:bg-[#A30D25] disabled:opacity-60"
          >
            <Save className="w-4 h-4" /> {saving ? "Guardando…" : "Guardar cambios"}
          </button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 text-sm bg-red-50 border border-red-200 text-red-700">{error}</div>}

      <MenuTree
        section="header"
        title="Header (barra de navegación principal)"
        groups={HEADER_GROUPS}
        config={config.header}
        onToggle={(k) => toggleHidden("header", k)}
        onDelete={(k, label) => removeEntry("header", k, label)}
        onMove={(k, dir, sibs) => move("header", k, dir, sibs)}
      />

      <MenuTree
        section="footer"
        title="Footer (pie de página)"
        groups={FOOTER_GROUPS}
        config={config.footer}
        onToggle={(k) => toggleHidden("footer", k)}
        onDelete={(k, label) => removeEntry("footer", k, label)}
        onMove={(k, dir, sibs) => move("footer", k, dir, sibs)}
      />
    </div>
  );
}

/**
 * Reusable menu tree editor — used identically for the header and the footer.
 * Renders each top-level group with its own hide / delete / reorder controls,
 * plus a nested list of sub-items with the same controls scoped to their
 * siblings.
 */
function MenuTree({ section, title, groups, config, onToggle, onDelete, onMove }) {
  const hidden = new Set(config.hidden || []);
  const order = config.order || [];
  const rank = new Map(order.map((k, i) => [k, i]));
  const rankOf = (k, fallbackIdx) => (rank.has(k) ? rank.get(k) : 10000 + fallbackIdx);
  const orderedGroups = [...groups]
    .map((g, i) => ({ ...g, __rank: rankOf(g.key, i) }))
    .sort((a, b) => a.__rank - b.__rank);

  const isHidden = (k) => hidden.has(k);

  return (
    <>
      <h2 className="font-serif text-xl text-[#0F2040] mb-4 mt-10 first:mt-0" data-testid={`menus-title-${section}`}>{title}</h2>
      <div className="bg-white border border-gray-200 divide-y divide-gray-100 mb-2">
        {orderedGroups.map((g, idx) => {
          const siblingKeys = orderedGroups.map((x) => x.key);
          const childrenOrdered = [...g.items]
            .map((it, i) => ({ ...it, __rank: rankOf(it.key, i) }))
            .sort((a, b) => a.__rank - b.__rank);
          const childKeys = childrenOrdered.map((x) => x.key);
          return (
            <div key={g.key} className="p-4">
              <RowControls
                section={section}
                itemKey={g.key}
                label={g.label}
                hidden={isHidden(g.key)}
                idx={idx}
                total={orderedGroups.length}
                onUp={() => onMove(g.key, -1, siblingKeys)}
                onDown={() => onMove(g.key, +1, siblingKeys)}
                onToggle={() => onToggle(g.key)}
                onDelete={() => onDelete(g.key, g.label)}
                arrowSize={4}
              />
              {childrenOrdered.length > 0 && (
                <div className="pl-9 mt-3 space-y-1.5">
                  {childrenOrdered.map((it, subIdx) => (
                    <RowControls
                      key={it.key}
                      section={section}
                      itemKey={it.key}
                      label={it.label}
                      hidden={isHidden(it.key)}
                      idx={subIdx}
                      total={childrenOrdered.length}
                      onUp={() => onMove(it.key, -1, childKeys)}
                      onDown={() => onMove(it.key, +1, childKeys)}
                      onToggle={() => onToggle(it.key)}
                      onDelete={() => onDelete(it.key, it.label)}
                      arrowSize={3}
                      subItem
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}

function RowControls({ section, itemKey, label, hidden, idx, total, onUp, onDown, onToggle, onDelete, arrowSize, subItem }) {
  const arrowClass = arrowSize === 3 ? "w-3 h-3" : "w-4 h-4";
  const btnBase = subItem ? "p-1" : "p-1.5";
  const labelSize = subItem ? "text-xs" : "text-sm";
  const labelColor = hidden ? "text-gray-400 line-through" : (subItem ? "text-gray-700" : "text-[#0F2040] font-medium");
  return (
    <div className={`flex items-center gap-3 ${subItem ? "text-xs" : ""}`}>
      <div className="flex flex-col">
        <button
          onClick={onUp}
          disabled={idx === 0}
          className="p-0.5 text-gray-400 hover:text-[#0F2040] disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid={`menus-up-${itemKey}`}
          title="Subir"
        >
          <ChevronUp className={arrowClass} />
        </button>
        <button
          onClick={onDown}
          disabled={idx === total - 1}
          className="p-0.5 text-gray-400 hover:text-[#0F2040] disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid={`menus-down-${itemKey}`}
          title="Bajar"
        >
          <ChevronDown className={arrowClass} />
        </button>
      </div>
      <div className="flex-1 min-w-0">
        <p className={`${labelSize} ${labelColor}`}>{label}</p>
        <p className="text-[11px] text-gray-400 font-mono truncate">{itemKey}</p>
      </div>
      <button
        onClick={onToggle}
        data-testid={`menus-toggle-${itemKey}`}
        className={`${btnBase} border ${
          hidden
            ? "border-red-200 text-red-600 hover:bg-red-50"
            : "border-gray-200 text-emerald-600 hover:bg-emerald-50"
        }`}
        title={hidden ? "Mostrar" : "Ocultar"}
      >
        {hidden ? <EyeOff className={arrowClass} /> : <Eye className={arrowClass} />}
      </button>
      <button
        onClick={onDelete}
        data-testid={`menus-delete-${itemKey}`}
        className={`${btnBase} border border-gray-200 text-gray-400 hover:border-red-500 hover:text-red-600 hover:bg-red-50`}
        title="Eliminar"
      >
        <Trash2 className={arrowClass} />
      </button>
    </div>
  );
}
